/**
 * Google Places lookup — the client half (2026-10-23).
 *
 * The server owns the key and the parsing (`monad/api/v1/places.py`); a picked
 * place comes back with its parts already shaped as CELL values (`location`,
 * `opening_periods`). This module only holds what every caller shares:
 *
 *  - `placesConfigQuery`: whether the lookup is offered at all. A server
 *    without a key (or a failed probe) means no search box — the editor works
 *    exactly as it does without places.
 *  - `newSessionToken`: one token per SEARCH SESSION (Google bills the
 *    keystrokes of a session plus its closing details call as one lookup), the
 *    same rule as Sentinel's Site form: a fresh token per open, renewed after
 *    each pick.
 *  - `PlacePickContext`: how a surface hosting several editors (the create
 *    form) hears that a place was picked, so it can fill its OTHER fields.
 *    Which fields take which part is declared per kind (`WidgetSpec.fromPlace`)
 *    and per role (`display.semantic`), never by slug.
 */
import { createContext } from 'react'
import { monadClient, type PlaceDetails } from '../api/client'
import { METADATA_GC_TIME, METADATA_STALE_TIME } from '../api/typeMetadata'

export const placesConfigKey = (slug: string) => ['places-config', slug] as const

/** Server configuration changes on a deploy, never mid-session: cache it like
 *  type metadata. A failed probe resolves to "not configured" (no retry storm,
 *  no error state — the lookup is an assist, not a dependency). */
export function placesConfigQuery(slug: string) {
  return {
    queryKey: placesConfigKey(slug),
    queryFn: async () => {
      try {
        return (await monadClient.getPlacesConfig(slug)).configured === true
      } catch {
        return false
      }
    },
    staleTime: METADATA_STALE_TIME,
    gcTime: METADATA_GC_TIME,
  }
}

export function newSessionToken(): string {
  return globalThis.crypto.randomUUID()
}

/** Called after a place is picked in a location editor, with the attribute
 *  that editor writes (already set — the host must not fill it again). */
export type PlacePicked = (place: PlaceDetails, sourceAttributeId: string | undefined) => void

export const PlacePickContext = createContext<PlacePicked | null>(null)

/** One field as the create form knows it — enough to decide what a place fills. */
export interface PlaceFillField {
  attribute_id: string
  type: string
  display?: { semantic?: unknown } | null
}

/**
 * The values a picked place gives a form's EMPTY fields. Each field's kind
 * answers for itself (`fromPlace` on its widget registration), told only its
 * declared role and whether it is the record's primary attribute. A field that
 * already holds a value, the field the pick came from, and a kind that declares
 * nothing are left alone.
 */
export function placeFill(
  fields: ReadonlyArray<PlaceFillField>,
  values: Record<string, unknown>,
  place: PlaceDetails,
  opts: {
    primaryId: string | undefined
    sourceAttributeId: string | undefined
    fromPlace: (kind: string) => ((place: PlaceDetails, role: PlaceRole) => unknown) | undefined
  },
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const f of fields) {
    if (f.attribute_id === opts.sourceAttributeId) continue
    const current = values[f.attribute_id]
    const empty = current === undefined || current === null || (typeof current === 'string' && current.trim() === '')
    if (!empty) continue
    const fill = opts.fromPlace(f.type)
    if (!fill) continue
    const semantic = typeof f.display?.semantic === 'string' ? f.display.semantic : undefined
    const v = fill(place, { primary: f.attribute_id === opts.primaryId, semantic })
    if (v === undefined || v === null || v === '') continue
    out[f.attribute_id] = v
  }
  return out
}

/** What a kind is told about the field it would fill. */
export interface PlaceRole {
  /** The record's primary (name) attribute, as `resolvePrimaryAttributeId` says. */
  primary: boolean
  /** The attribute's declared `display.semantic` role, if any. */
  semantic: string | undefined
}

/** The declared role of a text attribute that holds a Google place id
 *  (`monad/attribute_roles.py`, `semantic: place_id`). */
export const PLACE_ID_ROLE = 'place_id'
